/* eslint-disable @typescript-eslint/no-explicit-any */
import { AppBar, Drawer, Icon, IconButton, ListItem, ListItemIcon, ListItemText, Toolbar, Typography } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Loader from '../screen/CustomLoader';
import ConfirmationDialog from '../dialog/ConfirmationDialog';
import AnimatedLoader from '../../components/display/AnimatedLoader';

const iconsLocation = `${ASSET_BASE}/assets/images/icons`;

interface INavDrawerProps {
    isOpen : boolean;
    env : string;
    path : string;
    currentUser : any;
    isLoading : boolean;
    logOut : () => void;
    closeDrawer : () => void;
    onHelpClick : () => void;
    sections : Array<React.ReactElement>;
    onTicketSubmit ?: () => Promise<void>;
    zz2FreshSupportTicketDialog ?: React.ReactElement;
}

/**
 * @param {boolean} isOpen - Determines if the navigation drawer is open or closed. Required.
 * @param {string} env - The current environment (e.g., 'development', 'production'). Required.
 * @param {string} path - The current path or route in the application. Used to highlight active sections. Required.
 * @param {any} currentUser - The current user's information. This can be any type that represents user data. Required.
 * @param {boolean} isLoading - Flag to indicate whether the application or a part of it is loading. Used to show a loader or disable interactions. Required.
 * @param {() => void} logOut - Callback function to log out the current user. Required.
 * @param {() => void} closeDrawer - Callback function to close the navigation drawer. Required.
 * @param {() => void} onHelpClick - Callback function triggered when the help section is clicked. Required.
 * @param {Array<React.ReactElement>} sections - Array of React elements that represent different sections of the navigation drawer. Required.
 * @param {() => Promise<void>} [onTicketSubmit] - Optional callback function triggered when a support ticket is submitted. This function may return a promise that resolves when the ticket submission process completes.
 * @param {React.ReactElement} [zz2FreshSupportTicketDialog] - Optional React element for displaying the support ticket dialog. This element could be a modal or another type of UI component.
 * 
 * @return {React.ReactElement} The React element for the NavDrawer component.
 */
const NavDrawer = (props : INavDrawerProps) : React.ReactElement => {
    const [isLogOutDialogOpen, setIsLogOutDialogOpen] = useState<boolean>(false);

    const onLogoutClick = () => {
        setIsLogOutDialogOpen(true);
    };

    const handleLogOutConfirmation = () => {
        setIsLogOutDialogOpen(false);
        props.logOut();
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const renderFaqButton = useMemo<JSX.Element>(() => {
        return (
            <Link to={'/faq'} onClick={props.closeDrawer} style={{ textDecoration: 'unset', color: 'unset' }}>
                <ListItem button key={'FAQ'} className={'w300 pt10'}>
                    <ListItemIcon key={'icon FAQ'}>
                        <Icon fontSize={'large'} className={'h35 w35 cpd'} >live_help</Icon>
                    </ListItemIcon>
                    <ListItemText disableTypography className={'pl0'} inset primary={'FAQ'} />
                </ListItem>
            </Link>
        );
    }, [props.currentUser]);

    const renderSections = useMemo<JSX.Element>(() => {
        if (props.isLoading) {
            return <AnimatedLoader />;
        }

        return (   
            <div className={'fdc oa hidescroll w300 ml25 mb145'}>
                <div className={'flx1 fdc pt10'}>{props.sections}</div>
                <div className={'posf posb0 fdc'} >
                    <ListItem button key={'help'} onClick={props.onHelpClick} className={'w300'}>
                        <ListItemIcon key={'icon help'}>
                            <HelpOutlineIcon fontSize={'large'} className={'h35 w35 cpd pl1'}/>
                        </ListItemIcon>
                        <ListItemText disableTypography className={'pl0'} inset primary={'Help'} />
                    </ListItem>
                    {
                        renderFaqButton
                    }
                    <ListItem button key={'logout'} onClick={onLogoutClick} className={'w300'}>
                        <ListItemIcon key={'icon logout'}>
                            <img className={'h35 w35'} src={`${iconsLocation}/logout.svg`} />
                        </ListItemIcon>
                        <ListItemText disableTypography className={'fwb pl0'} inset primary={'LOGOUT'} />
                    </ListItem>                                
                </div>
            </div>
        );

    }, [props.isLoading, props.sections]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <Drawer variant='temporary' onBackdropClick={props.closeDrawer} anchor={'left'} open={props.isOpen} className={'usn'}>
                <div className={'w350 flx1 fdc oh'}>
                    <AppBar position='static' className={'wsnw h52 mxh52 usn'} elevation={0}>
                        <Toolbar variant='dense' className={'pr0'}>
                            <div className={'flx1 aic fdr'}>
                                <IconButton className={`mr20 ${props.isOpen ? 'cp' : 'cw'}`} disabled={props.isOpen} onClick={props.closeDrawer}>
                                    <Icon>menu</Icon>
                                </IconButton>
                                <Link to={'/'} className={'fdr aic mt5 mb5 tdus cw'} onClick={props.closeDrawer}>
                                    {<img height={42} src={`${ASSET_BASE}/assets/images/icons/${ICONS.project_logo}`} className={'mr10 cursorPointer disableImageDrag'}/>}
                                    <Typography color='inherit' variant='h6' className={'wsnw'}>
                                        {props.env}
                                    </Typography>
                                </Link>
                            </div>
                        </Toolbar>
                    </AppBar>
                    {
                        renderSections
                    }
                </div>
            </Drawer>
            <ConfirmationDialog
                isLoading={props.isLoading}
                open={isLogOutDialogOpen}
                title={'Log Out?'}
                description={'Are you sure you would like to logout?'}
                onAccept={handleLogOutConfirmation}
                onClose={() => setIsLogOutDialogOpen(false)}
                dialogType='red'
            />
            {
                props.zz2FreshSupportTicketDialog
            }
        </div>
    );
};

export default NavDrawer;
